import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Wallet,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import type { BillSlip } from "@boo/shared";
import { Button } from "@/components/ui/button";
import { fetchBillSlipUrl } from "@/services/billApi";
import { useBillStore } from "../store/useBillStore";
import type { BillEditor } from "../types/bills.types";

const message = (err: unknown) =>
  err instanceof Error ? err.message : "Something went wrong";

const SWIPE_MIN_PX = 80;

function SlipCard({
  billId,
  slip,
  onSwipe,
}: {
  billId: string;
  slip: BillSlip;
  onSwipe?: (dir: 1 | -1) => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const startX = useRef<number | null>(null);
  const dx = useRef(0);
  const dragged = useRef(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [busy, setBusy] = useState(false);
  const { confirmSlip, rejectSlip } = useBillStore.getState();

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    if (slip.isCash) return;
    async function loadSlip() {
      try {
        const u = await fetchBillSlipUrl(billId, slip.id);
        if (cancelled) URL.revokeObjectURL(u);
        else {
          url = u;
          setImageUrl(u);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    }
    void loadSlip();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [billId, slip.id, attempt]);

  async function act(fn: () => Promise<unknown>, ok?: string) {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
    } catch (err) {
      toast.error(message(err));
    } finally {
      setBusy(false);
    }
  }

  function move(x: number) {
    if (cardRef.current) {
      cardRef.current.style.transform = x ? `translateX(${x}px)` : "";
      cardRef.current.style.transition = x
        ? "none"
        : "transform 150ms ease-out";
    }
  }

  function endDrag() {
    if (startX.current === null) return;
    const d = dx.current;
    startX.current = null;
    dx.current = 0;
    move(0);
    if (dragged.current && Math.abs(d) > SWIPE_MIN_PX)
      onSwipe?.(d < 0 ? 1 : -1);
  }

  return (
    <div
      ref={cardRef}
      style={{ touchAction: onSwipe ? "pan-y" : undefined }}
      onPointerDown={(e) => {
        if (!onSwipe) return;
        startX.current = e.clientX;
        dragged.current = false;
      }}
      onPointerMove={(e) => {
        if (startX.current === null) return;
        const d = e.clientX - startX.current;
        if (!dragged.current && Math.abs(d) > 8) {
          dragged.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
        }
        if (dragged.current) {
          dx.current = d;
          move(d);
        }
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className="relative space-y-3 rounded-xl border border-primary/40 bg-card p-4"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-base font-semibold text-foreground">
          {slip.personName}
        </span>
        {slip.parsedAmount != null && (
          <span className="text-2xl font-semibold text-foreground [font-variant-numeric:tabular-nums]">
            ฿{slip.parsedAmount}
          </span>
        )}
      </div>
      {slip.reason && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2.5 text-sm font-medium text-foreground"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          {slip.reason}
        </div>
      )}

      {slip.isCash ? null : imageUrl ? (
        <button
          type="button"
          onClick={() => !dragged.current && setZoomed(true)}
          aria-label="Enlarge slip"
          className="block w-full rounded-lg bg-[var(--surface-muted)]"
        >
          <img
            src={imageUrl}
            alt={`Slip from ${slip.personName}`}
            className="max-h-[60dvh] w-full rounded-lg object-contain"
          />
        </button>
      ) : failed ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-lg bg-[var(--surface-muted)] text-sm text-muted-foreground">
          Could not load slip
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setFailed(false);
              setAttempt((n) => n + 1);
            }}
          >
            Retry
          </Button>
        </div>
      ) : (
        <div className="h-64 animate-pulse rounded-lg bg-[var(--surface-muted)]" />
      )}

      <div className="grid grid-cols-2 gap-3">
        <Button
          className="min-h-11"
          disabled={busy}
          onClick={() =>
            act(() => confirmSlip(slip.id), `${slip.personName} marked paid`)
          }
        >
          Mark paid
        </Button>
        <Button
          variant="outline"
          className="min-h-11 border-destructive/40 text-destructive"
          disabled={busy}
          onClick={() => act(() => rejectSlip(slip.id))}
        >
          Reject
        </Button>
      </div>

      {zoomed &&
        imageUrl &&
        !slip.isCash &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-x-0 top-0 z-[60] flex h-dvh items-center justify-center bg-black/85 p-4 pt-[calc(1rem+env(safe-area-inset-top))]"
            onClick={() => setZoomed(false)}
          >
            <button
              type="button"
              aria-label="Close"
              className="absolute right-4 top-[calc(1rem+env(safe-area-inset-top))] flex h-11 w-11 items-center justify-center rounded-full bg-card text-foreground"
              onClick={() => setZoomed(false)}
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={imageUrl}
              alt={`Slip from ${slip.personName}`}
              className="max-h-full max-w-full object-contain"
            />
          </div>,
          document.body,
        )}
    </div>
  );
}

export default function SlipReview({ editor }: { editor: BillEditor }) {
  const [index, setIndex] = useState(0);
  const count = editor.slips.length;
  if (!editor.billId || count === 0) return null;

  const current = index % count;
  const go = (dir: 1 | -1) => setIndex((current + dir + count) % count);
  const slip = editor.slips[current];

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Wallet className="h-4 w-4 text-primary" />
          Slips to review
        </h2>
        {count > 1 && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground [font-variant-numeric:tabular-nums]">
            <button
              type="button"
              aria-label="Previous slip"
              onClick={() => go(-1)}
              className="flex h-11 w-11 items-center justify-center rounded-full"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            {current + 1} of {count}
            <button
              type="button"
              aria-label="Next slip"
              onClick={() => go(1)}
              className="flex h-11 w-11 items-center justify-center rounded-full"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
      <div className="relative isolate overflow-x-clip pb-6">
        {Array.from({ length: Math.min(2, count - 1) }, (_, i) => i + 1).map(
          (i) => (
            <div
              key={i}
              aria-hidden
              className="absolute inset-x-0 top-0 h-full origin-bottom rounded-xl border border-border bg-card"
              style={{
                transform: `translateY(${i * 10}px) scale(${1 - i * 0.04})`,
                zIndex: -i,
              }}
            />
          ),
        )}
        <SlipCard
          key={slip.id}
          billId={editor.billId}
          slip={slip}
          onSwipe={count > 1 ? go : undefined}
        />
      </div>
    </section>
  );
}
